import { useReduxState } from 'hooks';
import { SiteSettingsForm } from 'components/admin/settings';
import { Button } from 'components/common';
import {
  ArrayField,
  Checkbox,
  Emails,
  FormGroup,
  RichText,
  Select,
  Switch,
  Text
} from 'components/common/form';
import { UserAttributesConditionsManager } from '.';

import type { UserCustomAttribute } from 'shared-types';
import type { GetUserCustomAttributesRes } from 'shared-types/responses';

interface Props {
  userCustomAttributes: GetUserCustomAttributesRes['userCustomAttributes'];
}

const UserAttributesNotifications = ({ userCustomAttributes }: Props) => {
  const { serverConfig, theme } = useReduxState();

  const initialValues = {
    userCustomAttributeNotifications:
      serverConfig.userCustomAttributeNotifications
  };

  return userCustomAttributes.length ? (
    <SiteSettingsForm
      initialValues={initialValues}
      title="User attributes notifications"
    >
      {({ values }) => (
        <ArrayField field="userCustomAttributeNotifications">
          {({ add }) => (
            <FormGroup label="Configured notifications" maxWidth="100%">
              <ArrayField.Items>
                {({ remove, index, field }) => (
                  <div className="notification-config">
                    <Switch
                      field={`${field}.isEnabled`}
                      id={`${field}-isEnabled`}
                      label="Enable email notification"
                    />

                    <FormGroup
                      label="Tracked attribute"
                      labelHelpText="Email notification will be sent if users set this value to true"
                    >
                      <Select
                        field={`${field}.trackedAttributeKey`}
                        id={`${field}-trackedAttributeKey`}
                        options={userCustomAttributes
                          .filter((attr) => attr.type === 'Boolean')
                          .map((attr) => ({
                            label: attr.title,
                            value: attr.key
                          }))}
                        style={{ width: 250 }}
                      />
                    </FormGroup>

                    {values.userCustomAttributeNotifications?.[index]
                      ?.trackedAttributeKey ? (
                      <>
                        <FormGroup label="Email frequency">
                          <Select
                            field={`${field}.emailFrequency`}
                            id={`${field}-emailFrequency`}
                            options={[
                              { label: 'Day', value: 'day' },
                              { label: 'Week', value: 'week' },
                              { label: 'Month', value: 'month' }
                            ]}
                            style={{ width: 250 }}
                          />
                        </FormGroup>

                        <FormGroup label="Email language">
                          <Select
                            field={`${field}.emailLanguage`}
                            id={`${field}-emailLanguage`}
                            options={serverConfig.supportedLanguages.map(
                              (lang) => ({ label: lang, value: lang })
                            )}
                            style={{ width: 250 }}
                          />
                        </FormGroup>

                        <FormGroup label="Email subject">
                          <Text
                            field={`${field}.emailSubject`}
                            id={`${field}-emailSubject`}
                          />
                        </FormGroup>

                        <FormGroup label="Email description">
                          <RichText
                            field={`${field}.emailDescription`}
                            id={`${field}-emailDescription`}
                          />
                        </FormGroup>

                        <FormGroup label="Attributes included in email">
                          <Select
                            field={`${field}.emailAttributeKeys`}
                            id={`${field}-emailAttributeKeys`}
                            isMulti={true}
                            options={userCustomAttributes
                              .filter(
                                (attr) =>
                                  attr.key !==
                                  values.userCustomAttributeNotifications?.[
                                    index
                                  ].trackedAttributeKey
                              )
                              .map((attr) => ({
                                label: attr.title,
                                value: attr.key
                              }))}
                          />
                        </FormGroup>

                        <FormGroup label="Send to these email addresses">
                          <Emails
                            field={`${field}.emailRecipientsOther`}
                            id={`${field}-emailRecipientsOther`}
                          />
                        </FormGroup>

                        <FormGroup label="Send to supervisors" maxWidth="100%">
                          <Checkbox
                            field={`${field}.emailRecipientsSupervisors.isEnabled`}
                            label="Send to supervisors"
                          />

                          {values.userCustomAttributeNotifications?.[index]
                            ?.emailRecipientsSupervisors?.isEnabled ? (
                            <UserAttributesConditionsManager
                              availableAttributes={userCustomAttributes
                                .filter(
                                  (attr) =>
                                    attr.key !==
                                    values.userCustomAttributeNotifications?.[
                                      index
                                    ].trackedAttributeKey
                                )
                                .reduce((attrs, attr) => {
                                  attrs[attr.key] = attr;
                                  return attrs;
                                }, {} as Record<string, UserCustomAttribute>)}
                              fieldPrefix={`${field}.emailRecipientsSupervisors`}
                              includeAuthenticationTypeCondition={true}
                              includeEmailCondition={true}
                              includeEmailDomainCondition={true}
                              includeIsSelfRegisteredCondition={true}
                            />
                          ) : null}
                        </FormGroup>
                      </>
                    ) : null}

                    <div className="notification-config-footer">
                      <Button
                        category="light"
                        icon="trash-alt"
                        onClick={remove}
                        size="small"
                        text="Remove notification"
                      />
                    </div>
                  </div>
                )}
              </ArrayField.Items>

              <Button
                category="light"
                icon="plus"
                onClick={add}
                size="small"
                style={{ marginTop: 10 }}
                text="Add notification"
              />

              <style jsx>{`
                .notification-config {
                  border: 1px solid ${theme.lightGreyDarkActive};
                  border-radius: ${theme.cardBorderRadius};
                  color: ${theme.textColorLight};
                  font-size: 1.6rem;
                  font-weight: 600;
                  margin: 10px 0;
                  padding: 15px;
                }
                .notification-config:not(:first-child) {
                  margin-top: 20px;
                }
                .notification-config-footer {
                  margin-top: 10px;
                  text-align: right;
                }
              `}</style>
            </FormGroup>
          )}
        </ArrayField>
      )}
    </SiteSettingsForm>
  ) : null;
};

export default UserAttributesNotifications;
